export interface IInlineImageInfo {
  originalUrl: string;
  filePath: string;
  placeholder: string;
}

/**
 * 記事本文のHTMLから <img> タグを抽出し、プレースホルダーに置換する
 *
 * @param articleId - 記事ID（ファイル名の一意性確保に使用）
 * @param bodyHtml - 記事本文のHTML
 * @returns modifiedBody: プレースホルダー置換済みのHTML, images: 抽出した画像情報
 */
export const extractInlineImageInfo = (
  articleId: number,
  bodyHtml: string,
): { modifiedBody: string; images: IInlineImageInfo[] } => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(bodyHtml, "text/html");
  const imgElements = doc.querySelectorAll("img");

  const images: IInlineImageInfo[] = [];
  let index = 1;

  imgElements.forEach((img) => {
    const src = img.getAttribute("src");
    if (!src) return;

    // data: URI や cid: 参照（メール埋め込み画像）はスキップ
    if (src.startsWith("data:") || src.startsWith("cid:")) return;

    // プロトコルなしURL（//example.com/...）をhttps:に補完
    const normalizedSrc = src.startsWith("//") ? `https:${src}` : src;

    // トラッキングURL等の非画像URLをスキップ
    if (isNonImageUrl(normalizedSrc)) return;

    const extension = extractExtensionFromUrl(normalizedSrc) || "png";
    const filePath = `inline_images/${articleId}_${index}.${extension}`;
    const placeholder = `{{${filePath}}}`;

    images.push({ originalUrl: normalizedSrc, filePath, placeholder });

    const textNode = doc.createTextNode(placeholder);
    img.parentNode?.replaceChild(textNode, img);

    index++;
  });

  const modifiedBody = doc.body.innerHTML;

  return { modifiedBody, images };
};

/**
 * Markdown本文から ![alt](url) 形式の画像を抽出し、プレースホルダーに置換する
 *
 * @param id - チケットIDなど（ファイル名の一意性確保に使用）
 * @param bodyMarkdown - Markdown形式の本文
 * @returns modifiedBody: プレースホルダー置換済みの本文, images: 抽出した画像情報
 */
export const extractMarkdownInlineImageInfo = (
  id: number,
  bodyMarkdown: string,
  startIndex: number = 1,
): { modifiedBody: string; images: IInlineImageInfo[] } => {
  const images: IInlineImageInfo[] = [];
  let index = startIndex;

  // ![alt](url) パターンにマッチする正規表現
  const markdownImageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;

  const modifiedBody = bodyMarkdown.replace(
    markdownImageRegex,
    (_match, _alt: string, url: string) => {
      const trimmedUrl = url.trim();

      // data: URI や cid: 参照（メール埋め込み画像）はスキップ
      if (trimmedUrl.startsWith("data:") || trimmedUrl.startsWith("cid:")) return _match;

      // トラッキングURL等の非画像URLをスキップ
      if (isNonImageUrl(trimmedUrl)) return _match;

      const extension = extractExtensionFromUrl(trimmedUrl) || "png";
      const filePath = `inline_images/${id}_${index}.${extension}`;
      const placeholder = `{{${filePath}}}`;

      images.push({ originalUrl: trimmedUrl, filePath, placeholder });
      index++;

      return placeholder;
    },
  );

  return { modifiedBody, images };
};

/**
 * URLパスからファイル拡張子を抽出する
 *
 * @param url - 画像のURL
 * @returns 画像拡張子（不明な場合はnull）
 */
export const extractExtensionFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split("/").pop() || "";
    const dotIndex = lastSegment.lastIndexOf(".");
    if (dotIndex === -1) return null;
    const ext = lastSegment.substring(dotIndex + 1).toLowerCase();
    const validExtensions = [
      "png",
      "jpg",
      "jpeg",
      "gif",
      "svg",
      "webp",
      "bmp",
      "ico",
    ];
    return validExtensions.includes(ext) ? ext : null;
  } catch {
    return null;
  }
};

/**
 * トラッキングURL等、画像ではないURLかどうかを判定する
 */
const isNonImageUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();

    // トラッキング系ドメインを除外
    const trackingDomains = [
      "hubspotlinks.com",
      "hubapi.com",
      "click.",
      "track.",
      "open.",
      "trk.",
      "t.co",
      "bit.ly",
      "goo.gl",
      "mailchimp.com",
      "list-manage.com",
      "sendgrid.net",
      "mktotracking.com",
      "pardot.com",
    ];
    if (trackingDomains.some((d) => hostname.includes(d))) return true;

    // URLパスに画像拡張子がなく、Zendesk以外のドメインの場合はスキップ
    const hasImageExtension = extractExtensionFromUrl(url) !== null;
    const isZendeskUrl = hostname.includes("zendesk.com") || hostname.includes("zdassets.com");
    if (!hasImageExtension && !isZendeskUrl) return true;

    return false;
  } catch {
    return false;
  }
};
